import { getCompany } from 'helpers/getCompanyVariables';
import { getStateNameFromStateCode } from 'helpers/states';

import {
  getTodaysDateAsInt,
  isDateValid
} from 'helpers/appointments/appointments';

import {
  buildSuncareRequiredActions,
  getSuncarePhase,
  getSuncareContractType,
  getSuncareQuestionnaireAnswers,
  isSuncareContext,
  isSuncareContractTypeDeterminate
} from 'helpers/suncare/suncare';

const company = getCompany();

export const KAISER_GA_REQUIRED_FIELDS = {
  kaiserGaSoldPolicyAcknowledgement: false,
  kaiserGaPolicyholderFullName: '',
  kaiserGaPolicyholderDob: null,
  kaiserGaPolicyholderSsn: ''
};

export const KAISER_GA_TRANSIENT_FIELDS = [
  'kaiserGaPolicyholderFullName',
  'kaiserGaPolicyholderDob',
  'kaiserGaPolicyholderSsn'
];

export function getCurrentAction(requiredActions, formData) {
  let currentIndex = requiredActions.length - 1;

  for (let index = 0; index < requiredActions.length; index++) {
    const action = requiredActions[index];
    if (!isStepValid(index, action, formData)) {
      currentIndex = index;
      break;
    }
  }
  return currentIndex;
}

function getAcknowledgementMessage(carrierName) {
  if (carrierName === 'Oscar' || carrierName === 'CareSource') {
    return 'I attest that I have sent a copy of this form to the carrier as instructed above.';
  }
  return '';
}

export function isStepValid(index, action, formData, specialAcknowledgement) {
  if (action?.acknowledgementMessage && !specialAcknowledgement) {
    return false;
  }

  if (action?.type === 'acknowledgement') {
    return formData.acknowledgement;
  }

  if (action?.type === 'providenceAcknowledgement') {
    return formData.providenceAcknowledgement;
  }

  if (action?.type === 'bcbsNcAcknowledgement') {
    return formData.bcbsNcAcknowledgement;
  }

  if (action?.type === 'bcbsScAcknowledgement') {
    return formData.bcbsScAcknowledgement;
  }

  if (action?.type === 'kaiserGaSoldPolicyAcknowledgement') {
    return (
      !!formData.kaiserGaSoldPolicyAcknowledgement &&
      !!formData.kaiserGaPolicyholderFullName?.trim() &&
      !!formData.kaiserGaPolicyholderDob &&
      !!formData.kaiserGaPolicyholderSsn?.trim()
    );
  }

  if (action?.type === 'suncareContractSigning') {
    const answers = getSuncareQuestionnaireAnswers(formData);
    return (
      isSuncareContractTypeDeterminate(answers) &&
      !!getSuncareContractType(answers) &&
      formData.suncareContractOpened === true
    );
  }

  if (action?.type === 'suncareTrainingNotice') {
    return true;
  }

  if (action?.type === 'fileUpload') {
    const files = formData.files[index] || [];
    const expirationDate = formData.expirationDates[index];
    const hasDefaultExpirationDate = !!action?.defaultExpirationDate;

    const dateValid = !!expirationDate
      ? (() => {
          const { $d: date } = expirationDate;
          return isDateValid(date).valid;
        })()
      : false;

    if (action?.required && action?.expires && !hasDefaultExpirationDate) {
      return Array.isArray(files) && files.length > 0 && dateValid;
    }

    if (action?.required && (!action?.expires || hasDefaultExpirationDate)) {
      return Array.isArray(files) && files.length > 0;
    }

    if (!action?.required && !action?.expires) {
      return true;
    }

    if (!action?.required && action?.expires && !hasDefaultExpirationDate) {
      const hasFile = Array.isArray(files) && files.length > 0;
      return (!hasFile && !expirationDate) || (hasFile && dateValid);
    }

    if (!action?.required && (!action?.expires || hasDefaultExpirationDate)) {
      const hasFile = Array.isArray(files) && files.length > 0;
      return hasFile || !hasFile;
    }
  }

  return false;
}

export function isBlackoutPeriod(value, appointments = []) {
  if (!value) return { isBlackout: false, states: [] };

  const today = new Date();
  const cleanValue = value.replace(',', '').replace(/\s+/g, ' ').trim();
  const tokens = cleanValue.split(' ');

  const foundStates = [];
  let pendingStates = [];

  const isDateFormat = text => text.includes('/');

  const isValidDate = (month, day) => {
    if (month < 1 || month > 12) return false;

    const daysInMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (
      month === 2 &&
      today.getFullYear() % 4 === 0 &&
      (today.getFullYear() % 100 !== 0 || today.getFullYear() % 400 === 0)
    ) {
      return day >= 1 && day <= 29;
    }

    return day >= 1 && day <= daysInMonth[month];
  };

  const isTodayInRange = range => {
    const parts = range.split('-');

    if (parts.length === 1) {
      const dateParts = parts[0].split('/');
      if (dateParts.length !== 2) return false;

      const month = Number(dateParts[0]);
      const day = Number(dateParts[1]);

      if (isNaN(month) || isNaN(day) || !isValidDate(month, day)) return false;

      const blackoutDate = new Date(today.getFullYear(), month - 1, day);
      return (
        blackoutDate.getDate() === today.getDate() &&
        blackoutDate.getMonth() === today.getMonth() &&
        blackoutDate.getFullYear() === today.getFullYear()
      );
    }

    if (parts.length === 2) {
      const startParts = parts[0].split('/');
      const endParts = parts[1].split('/');

      if (
        (startParts.length !== 2 && startParts.length !== 3) ||
        (endParts.length !== 2 && endParts.length !== 3)
      ) {
        return false;
      }

      const parseDate = dateParts => {
        const month = parseInt(dateParts[0], 10);
        const day = parseInt(dateParts[1], 10);
        const year = dateParts[2]
          ? parseInt(dateParts[2], 10)
          : today.getFullYear();

        if (isNaN(month) || isNaN(day) || !isValidDate(month, day)) return null;

        return new Date(year, month - 1, day);
      };

      const startDate = parseDate(startParts);
      const endDate = parseDate(endParts);

      if (!startDate || !endDate) return false;

      if (startDate > endDate) {
        if (today >= startDate || today <= endDate) {
          return true;
        }
      } else {
        if (today >= startDate && today <= endDate) {
          return true;
        }
      }
    }

    return false;
  };

  for (const token of tokens) {
    if (!isDateFormat(token)) {
      const stateName = getStateNameFromStateCode(token);
      if (stateName) {
        pendingStates.push(stateName);
      }
    } else {
      if (pendingStates.length === 0) {
        return {
          isBlackout: isTodayInRange(token),
          states: []
        };
      } else {
        if (isTodayInRange(token)) {
          const validStates = pendingStates.filter(state =>
            appointments.includes(state)
          );
          foundStates.push(...validStates);
        }
        pendingStates = [];
      }
    }
  }

  if (foundStates.length > 0) {
    return {
      isBlackout: true,
      states: foundStates
    };
  } else {
    return {
      isBlackout: false,
      states: []
    };
  }
}

function sortRequiredActions(actions) {
  const requiredActions = actions.filter(action => action?.required);
  const optionalActions = actions.filter(action => !action?.required);

  return [...requiredActions, ...optionalActions];
}

// function to get the required actions based on the attachments that have been uploaded
function getFilteredActions(requiredActions, attachments, carrierName = '') {
  const formattedCarrierName = carrierName.replace(/\s/g, '_');
  // get current date in mmddyyyy format
  const currentDate = getTodaysDateAsInt();

  // cases where action is required:
  // 1. action type is acknowledgement
  // 2. attachment hasn't been uploaded
  // 3. attachment has expired (date on filename is less than current date)
  return requiredActions.filter(action => {
    if (action.suncareBypassFilter) {
      return true;
    }

    if (action.type === 'acknowledgement') {
      return true;
    }

    if (action.type === 'kaiserGaSoldPolicyAcknowledgement') {
      return true;
    }

    const { name, state, expires, carrierBased } = action;
    const formattedStateName = state ? state.replace(/\s/g, '_') : null;

    // since we're checking carrierBased, expires and state, the # of conditions is 2*2*2 = 8
    // carrierBased = true/false
    // expires = true/false
    // state = true/false

    // pattern to match the file name based on the various conditions
    const filePattern = (() => {
      // 1. carrierBased=true, expires=true, state!=null
      if (carrierBased && expires && formattedStateName) {
        return new RegExp(
          `^${formattedCarrierName}-${name}-\\d{8}-${formattedStateName}`
        );
      }
      // 2. carrierBased=false, expires=true, state!=null
      else if (!carrierBased && expires && formattedStateName) {
        return new RegExp(`^${name}-\\d{8}-${formattedStateName}`);
      }
      // 3. carrierBased=false, expires=false, state!=null
      else if (!carrierBased && !expires && formattedStateName) {
        return new RegExp(`^${name}-${formattedStateName}`);
      }
      // 4. carrierBased=false, expires=false, state=null
      else if (!carrierBased && !expires && !formattedStateName) {
        return new RegExp(`^${name}`);
      }
      // 5. carrierBased=false, expires=true, state=null
      else if (!carrierBased && expires && !formattedStateName) {
        return new RegExp(`^${name}-\\d{8}`);
      }
      // 6. carrierBased=true, expires=true, state=null
      else if (carrierBased && expires && !formattedStateName) {
        return new RegExp(`^${formattedCarrierName}-${name}-\\d{8}`);
      }
      // 7. carrierBased=true, expires=false, state=null
      else if (carrierBased && !expires && !formattedStateName) {
        return new RegExp(`^${formattedCarrierName}-${name}`);
      }
      // 8. carrierBased=true, expires=false, state!=null
      else if (carrierBased && !expires && formattedStateName) {
        return new RegExp(
          `^${formattedCarrierName}-${name}-${formattedStateName}`
        );
      }
    })();

    const matchingAttachments = attachments.filter(attachment =>
      filePattern.test(attachment.name)
    );

    if (matchingAttachments.length === 0) {
      return true;
    } else {
      // only check expiration date if the action has expires=true
      if (!expires) {
        return false; // if there's a matching attachment and no expiration, the action is not required
      }

      return !matchingAttachments.some(attachment => {
        // get date from filename based on pattern
        const dateMatch = attachment.name.match(/-(\d{8})(?:-|$)/);

        if (dateMatch && dateMatch[1]) {
          const fileDate = parseInt(dateMatch[1]);
          return fileDate >= currentDate; // File is not expired
        }
        return false;
      });
    }
  });
}

const acceptedTypes = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg'],
  'image/jpg': ['.jpg'],
  'application/pdf': ['.pdf']
};

export function getCarrierRequiredActions(
  carrierData,
  appointments,
  attachments,
  appointedType,
  transferBlackoutData,
  contractingBlackoutData,
  user = null
) {
  const requiredActions = [];

  // Blackout Information
  const { isBlackout: isTransferBlackout, states: transferBlackoutStates } =
    transferBlackoutData;
  const {
    isBlackout: isContractingBlackout,
    states: contractingBlackoutStates
  } = contractingBlackoutData;

  const isFullTransferBlackout =
    isTransferBlackout && transferBlackoutStates.length === 0;
  const isFullContractingBlackout =
    isContractingBlackout && contractingBlackoutStates.length === 0;

  if (company === 'sunshine') {
    const filteredStateExchangeStates =
      carrierData.stateBasedExchangeCertificateFile?.filter(state =>
        appointments?.includes(state)
      );
    const filteredStateInsuranceStates =
      carrierData.stateInsuranceLicenseFile?.filter(state =>
        appointments?.includes(state)
      );
    const filteredCustomStates = carrierData.customFileStates?.filter(state =>
      appointments?.includes(state)
    );

    if (
      carrierData.ffmIdFile &&
      appointments.some(
        state => !carrierData.stateBasedExchangeCertificateFile?.includes(state)
      )
    ) {
      requiredActions.push({
        title: 'Upload your FFM Certificate',
        description: 'Please upload a copy of your FFM Certificate',
        accept: acceptedTypes,
        expires: false,
        required: true,
        state: null,
        name: 'FFM_ID',
        type: 'fileUpload',
        carrierBased: false,
        defaultExpirationDate: null
      });
    }

    if (carrierData.driversLicenseFile) {
      requiredActions.push({
        title: 'Upload your Driver’s License',
        description: 'Please upload a copy of your driver’s license',
        accept: acceptedTypes,
        expires: true,
        required: true,
        state: null,
        name: 'Drivers_License',
        type: 'fileUpload',
        carrierBased: false,
        defaultExpirationDate: null
      });
    }

    if (filteredStateExchangeStates && filteredStateExchangeStates.length > 0) {
      const defaultExpirationDate = (() => {
        const currentYear = new Date().getFullYear();
        const expirationDate = parseInt(`1031${currentYear}`);
        const currentDate = getTodaysDateAsInt();
        if (expirationDate < currentDate) {
          return `1031${currentYear + 1}`;
        }
        return `1031${currentYear}`;
      })();

      filteredStateExchangeStates.forEach(state => {
        requiredActions.push({
          title: 'Upload your State Exchange Certificate',
          description: `Please upload a copy of your state exchange certificate for ${state}`,
          accept: acceptedTypes,
          expires: true,
          required: true,
          state,
          name: 'State_Exchange_Certificate',
          type: 'fileUpload',
          carrierBased: false,
          defaultExpirationDate
        });
      });
    }

    if (
      filteredStateInsuranceStates &&
      filteredStateInsuranceStates.length > 0
    ) {
      filteredStateInsuranceStates.forEach(state => {
        requiredActions.push({
          title: 'Upload your State Insurance License',
          description: `Please upload a copy of your state insurance license for ${state}`,
          accept: acceptedTypes,
          expires: true,
          required: true,
          state,
          name: 'State_Insurance_License',
          type: 'fileUpload',
          carrierBased: false,
          defaultExpirationDate: null
        });
      });
    }

    if (carrierData.customFileTitle) {
      const defaultExpirationDate = (() => {
        const today = new Date();
        const expiration = new Date(today);
        expiration.setFullYear(today.getFullYear() + 1);

        const year = expiration.getFullYear();
        const month = String(expiration.getMonth() + 1).padStart(2, '0');
        const day = String(expiration.getDate()).padStart(2, '0');

        return `${month}${day}${year}`;
      })();

      if (filteredCustomStates && filteredCustomStates.length > 0) {
        filteredCustomStates.forEach(state => {
          requiredActions.push({
            title: `Upload your ${carrierData.customFileTitle}`,
            description: `Please upload a copy of your ${carrierData.customFileTitle} for ${state}`,
            accept: acceptedTypes,
            expires: false,
            required: true,
            state,
            name: carrierData.customFileTitle.replace(/\s/g, '_'),
            type: 'fileUpload',
            carrierBased: false,
            defaultExpirationDate
          });
        });
      } else if (carrierData.customFileStates?.length === 0) {
        requiredActions.push({
          title: `Upload your ${carrierData.customFileTitle}`,
          description: `Please upload a copy of your ${carrierData.customFileTitle}`,
          accept: acceptedTypes,
          expires: false,
          required: true,
          state: null,
          name: carrierData.customFileTitle.replace(/\s/g, '_'),
          type: 'fileUpload',
          carrierBased: false,
          defaultExpirationDate
        });
      }
    }
  } else if (company === 'oneill') {
    if (
      carrierData.name === 'United Health Care' &&
      (appointedType.includes('Carrier') || appointedType.includes('Agency'))
    ) {
      requiredActions.push({
        title: 'Upload the Signed Transfer Form',
        description: 'Please upload a copy of the signed Transfer Form',
        accept: acceptedTypes,
        expires: false,
        required: true,
        state: null,
        url: null,
        transferNote: carrierData.transferNote,
        name: 'Transfer_Form',
        type: 'fileUpload',
        carrierBased: true,
        defaultExpirationDate: null,
        acknowledgementMessage: ''
      });
    }
    // Transfer - Direct | NO Full Transfer Blackout
    if (appointedType.includes('Carrier') && !isFullTransferBlackout) {
      if (
        !!carrierData.directFormURL &&
        (carrierData.name !== 'BCBS' ||
          !(
            (appointments.includes('Tennessee') &&
              transferBlackoutStates.includes('Tennessee')) ||
            (appointments.includes('Texas') &&
              transferBlackoutStates.includes('Texas'))
          ))
      ) {
        requiredActions.push({
          title: 'Upload the Signed Transfer Form',
          description: 'Please upload a copy of the signed Transfer Form',
          accept: acceptedTypes,
          expires: false,
          required: true,
          state: null,
          url: carrierData.directFormURL,
          transferNote: carrierData.transferNote,
          name: 'Direct_Transfer_Form',
          type: 'fileUpload',
          carrierBased: true,
          defaultExpirationDate: null,
          acknowledgementMessage: getAcknowledgementMessage(carrierData.name)
        });
      }
      if (
        !!carrierData.BCBSTNFormURL &&
        carrierData.name === 'BCBS' &&
        appointments.includes('Tennessee') &&
        !transferBlackoutStates.includes('Tennessee')
      ) {
        requiredActions.push({
          title: 'Upload the Signed Transfer Form',
          description: 'Please upload a copy of the signed Transfer Form',
          accept: acceptedTypes,
          expires: false,
          required: true,
          state: 'Tennessee',
          url: carrierData.BCBSTNFormURL,
          transferNote: carrierData.BCBSTNTransferNote,
          name: 'Direct_Transfer_Form',
          type: 'fileUpload',
          carrierBased: true,
          defaultExpirationDate: null
        });
      }
      if (
        !!carrierData.BCBSTXFormURL &&
        carrierData.name === 'BCBS' &&
        appointments.includes('Texas') &&
        !transferBlackoutStates.includes('Texas')
      ) {
        requiredActions.push({
          title: 'Upload the Signed Transfer Form',
          description: 'Please upload a copy of the signed Transfer Form',
          accept: acceptedTypes,
          expires: false,
          required: true,
          state: 'Texas',
          url: carrierData.BCBSTXFormURL,
          transferNote: carrierData.BCBSTXTransferNote,
          name: 'Direct_Transfer_Form',
          type: 'fileUpload',
          carrierBased: true,
          defaultExpirationDate: null
        });
      }
    }
    // Transfer - Agency | NO Full Transfer Blackout
    else if (appointedType.includes('Agency') && !isFullTransferBlackout) {
      if (
        !!carrierData.agencyFormURL &&
        (carrierData.name !== 'BCBS' ||
          !(
            (appointments.includes('Tennessee') &&
              transferBlackoutStates.includes('Tennessee')) ||
            (appointments.includes('Texas') &&
              transferBlackoutStates.includes('Texas'))
          ))
      ) {
        requiredActions.push({
          title: 'Upload the Signed Transfer Form',
          description: 'Please upload a copy of the signed Transfer Form',
          accept: acceptedTypes,
          expires: false,
          required: true,
          state: null,
          url: carrierData.agencyFormURL,
          transferNote: carrierData.transferNote,
          name: 'Agency_Transfer_Form',
          type: 'fileUpload',
          carrierBased: true,
          defaultExpirationDate: null,
          acknowledgementMessage: getAcknowledgementMessage(carrierData.name)
        });
      }
      if (
        !!carrierData.BCBSTNFormURL &&
        carrierData.name === 'BCBS' &&
        appointments.includes('Tennessee') &&
        !transferBlackoutStates.includes('Tennessee')
      ) {
        requiredActions.push({
          title: 'Upload the Signed Transfer Form',
          description: 'Please upload a copy of the signed Transfer Form',
          accept: acceptedTypes,
          expires: false,
          required: true,
          state: 'Tennessee',
          url: carrierData.BCBSTNFormURL,
          transferNote: carrierData.BCBSTNTransferNote,
          name: 'Agency_Transfer_Form',
          type: 'fileUpload',
          carrierBased: true,
          defaultExpirationDate: null
        });
      }
      if (
        !!carrierData.BCBSTXFormURL &&
        carrierData.name === 'BCBS' &&
        appointments.includes('Texas') &&
        !transferBlackoutStates.includes('Texas')
      ) {
        requiredActions.push({
          title: 'Upload the Signed Transfer Form',
          description: 'Please upload a copy of the signed Transfer Form',
          accept: acceptedTypes,
          expires: false,
          required: true,
          state: 'Texas',
          url: carrierData.BCBSTXFormURL,
          transferNote: carrierData.BCBSTXTransferNote,
          name: 'Agency_Transfer_Form',
          type: 'fileUpload',
          carrierBased: true,
          defaultExpirationDate: null
        });
      }
    }
    // New Appointment
    else {
      if (!isFullContractingBlackout) {
        if (
          !!carrierData.acknowledgementNote &&
          !appointments?.every(state =>
            contractingBlackoutStates.includes(state)
          )
        ) {
          requiredActions.push({
            title: 'Acknowledgement Notice',
            description: carrierData.acknowledgementNote,
            accept: null,
            expires: false,
            required: true,
            state: null,
            name: 'Acknowledgement',
            type: 'acknowledgement',
            carrierBased: false,
            defaultExpirationDate: null
          });
        }

        const filteredSBEtates =
          carrierData.SBEStates?.filter(state =>
            appointments?.includes(state)
          ).filter(state => !contractingBlackoutStates.includes(state)) || [];

        const filteredLOAStates =
          carrierData.LOAStates?.filter(state =>
            appointments?.includes(state)
          ).filter(state => !contractingBlackoutStates.includes(state)) || [];

        if (!!carrierData.SBEFileRequirement && filteredSBEtates.length > 0) {
          // every SBE expires and auto renews on 10/31
          // if the current date is past 10/31 of this year, set the expiration date to 10/31 of next year
          const defaultExpirationDate = (() => {
            const currentYear = new Date().getFullYear();
            const expirationDate = parseInt(`1031${currentYear}`);
            const currentDate = getTodaysDateAsInt();
            if (expirationDate < currentDate) {
              return `1031${currentYear + 1}`;
            }
            return `1031${currentYear}`;
          })();
          filteredSBEtates.forEach(state => {
            requiredActions.push({
              title: 'Upload your State Based Certificate',
              description: `Please upload a copy of your State Based Certificate for ${state}`,
              accept: acceptedTypes,
              expires: true,
              required: carrierData.SBEFileRequirement === 'Required',
              state,
              name: 'SBE',
              type: 'fileUpload',
              carrierBased: false,
              defaultExpirationDate
            });
          });
        }

        if (!!carrierData.LOAFileRequirement && filteredLOAStates.length > 0) {
          filteredLOAStates.forEach(state => {
            requiredActions.push({
              title: 'Upload your Line of Authority (LOA)',
              description: `Please upload a copy of your LOA for ${state}`,
              accept: acceptedTypes,
              expires: true,
              required: carrierData.LOAFileRequirement === 'Required',
              state,
              name: 'LOA',
              type: 'fileUpload',
              carrierBased: false,
              defaultExpirationDate: null
            });
          });
        }

        if (
          !!carrierData.PaperContractRequirement &&
          !appointments?.every(state =>
            contractingBlackoutStates.includes(state)
          )
        ) {
          requiredActions.push({
            title: 'Upload the Paper Contract',
            description: 'Please upload a copy of the paper contract',
            accept: acceptedTypes,
            expires: false,
            required: carrierData.PaperContractRequirement === 'Required',
            state: null,
            url: carrierData.PaperContractURL,
            name: 'Paper_Contract',
            type: 'fileUpload',
            carrierBased: true,
            defaultExpirationDate: null
          });
        }

        if (
          !!carrierData.floridaStateLicense &&
          appointments?.includes('Florida') &&
          !contractingBlackoutStates.includes('Florida')
        ) {
          requiredActions.push({
            title: 'Upload your Insurance License',
            description: 'Please upload a copy of your Insurance License',
            accept: acceptedTypes,
            expires: true,
            required: true,
            state: 'Florida',
            name: 'Insurance_License',
            type: 'fileUpload',
            carrierBased: false,
            defaultExpirationDate: null
          });
        }

        if (
          !!carrierData.ffmCertificate &&
          !appointments?.every(state =>
            contractingBlackoutStates.includes(state)
          )
        ) {
          // if the current date is past 12/31 of this year, set the expiration date to 12/31 of next year
          const defaultExpirationDate = (() => {
            const currentYear = new Date().getFullYear();
            const expirationDate = parseInt(`1231${currentYear}`);
            const currentDate = getTodaysDateAsInt();
            if (expirationDate < currentDate) {
              return `1231${currentYear + 1}`;
            }
            return `1231${currentYear}`;
          })();
          requiredActions.push({
            title: 'Upload your FFM Certificate',
            description:
              'Please upload a copy of your FFM Certificate for the current Plan Year.',
            accept: acceptedTypes,
            expires: true,
            required: true,
            state: null,
            name: 'FFM_Certificate',
            type: 'fileUpload',
            carrierBased: false,
            defaultExpirationDate
          });
        }

        if (
          !!carrierData.eoPolicy &&
          !appointments?.every(state =>
            contractingBlackoutStates.includes(state)
          )
        ) {
          requiredActions.push({
            title: 'Upload your Errors and Omissions Policy',
            description:
              'Please upload a copy of your E&O that <strong>has your name listed as the insured</strong>. You may provide an Addendum or Letter of Attestation in lieu of the policy page, which states that you are covered by the E&O policy you have provided. <strong>The Addendum or Letter of Attestation must include the policy number, effective date, expiration date, and insurer.</strong>',
            accept: acceptedTypes,
            expires: true,
            required: true,
            state: null,
            name: 'E&O_Policy',
            type: 'fileUpload',
            carrierBased: false,
            defaultExpirationDate: null
          });
        }

        if (carrierData.name === 'BCBS') {
          if (
            !!carrierData.BCBSNCPaperContractURL &&
            appointments?.includes('North Carolina') &&
            !contractingBlackoutStates.includes('North Carolina')
          ) {
            requiredActions.push({
              title: 'Upload the BCBS NC Paper Contract',
              description: 'Please upload a copy of the BCBS NC paper contract',
              accept: acceptedTypes,
              expires: false,
              required: true,
              state: 'North Carolina',
              url: carrierData.BCBSNCPaperContractURL,
              name: 'Paper_Contract',
              type: 'fileUpload',
              carrierBased: true,
              defaultExpirationDate: null
            });
          }

          if (
            !!carrierData.BCBSSCPaperContractURL &&
            appointments?.includes('South Carolina') &&
            !contractingBlackoutStates.includes('South Carolina')
          ) {
            requiredActions.push({
              title: 'Upload the BCBS SC Paper Contract',
              description: 'Please upload a copy of the BCBS SC paper contract',
              accept: acceptedTypes,
              expires: false,
              required: true,
              state: 'South Carolina',
              url: carrierData.BCBSSCPaperContractURL,
              name: 'Paper_Contract',
              type: 'fileUpload',
              carrierBased: true,
              defaultExpirationDate: null
            });
          }
        }
      }
    }

    if (carrierData.name === 'Providence') {
      requiredActions.push({
        title: 'Providence Acknowledgement Notice',
        description:
          'I acknowledge that I must sell at least 20 policies with Providence within 6 months of my appointment. I understand that not meeting this requirement may result in my appointment being terminated by the carrier and a waiting period before I can be reappointed.',
        accept: null,
        expires: false,
        required: true,
        state: null,
        name: 'Acknowledgement',
        type: 'providenceAcknowledgement',
        carrierBased: false,
        defaultExpirationDate: null
      });
    }

    if (
      carrierData.name === 'BCBS' &&
      appointments.includes('North Carolina')
    ) {
      requiredActions.push({
        title: 'BCBS Acknowledgement Notice',
        description:
          'I acknowledge that I must sell at least 50 policies with BCBS North Carolina within 12 months of my appointment. I understand that not meeting this requirement may result in my appointment being terminated by the carrier and a waiting period before I can be reappointed.',
        accept: null,
        expires: false,
        required: true,
        state: 'North Carolina',
        name: 'Acknowledgement',
        type: 'bcbsNcAcknowledgement',
        carrierBased: false,
        defaultExpirationDate: null
      });
    }

    if (
      carrierData.name === 'BCBS' &&
      appointments.includes('South Carolina')
    ) {
      requiredActions.push({
        title: 'BCBS Acknowledgement Notice',
        description:
          'I acknowledge that I must sell at least 25 policies with BCBS South Carolina within 12 months of my appointment. I understand that not meeting this requirement may result in my appointment being terminated by the carrier and a waiting period before I can be reappointed.',
        accept: null,
        expires: false,
        required: true,
        state: 'South Carolina',
        name: 'Acknowledgement',
        type: 'bcbsScAcknowledgement',
        carrierBased: false,
        defaultExpirationDate: null
      });
    }
  }

  // sort the required actions by required and optional
  const sortedBase = sortRequiredActions(requiredActions);

  const kaiserGaSoldPolicyDescriptionEn =
    '<p><strong>Kaiser Permanente – Georgia Requirement</strong></p><p>You must have at least one <strong>active sold Kaiser Permanente policy in Georgia</strong> to request an appointment.</p><p>You will also need to provide the policyholder’s <strong>name, date of birth, and Social Security number</strong>.</p><p>Please confirm you meet these requirements to continue.</p>';
  const kaiserGaSoldPolicyDescriptionEs =
    '<p><strong>Requisito de Kaiser Permanente – Georgia</strong></p><p>Debe contar con al menos una <strong>póliza activa vendida de Kaiser Permanente en Georgia</strong> para solicitar un appointment.</p><p>También deberá proporcionar el <strong>nombre, la fecha de nacimiento y el número de Seguro Social</strong> del titular de la póliza.</p><p>Por favor confirme que cumple con estos requisitos para continuar.</p><br/>';

  const kaiserGaSoldPolicyAcknowledgementStep =
    company === 'sunshine' &&
    carrierData.name?.trim() === 'Kaiser Permanente' &&
    appointments?.includes('Georgia')
      ? [
          {
            title: 'Sold policy requirement',
            descriptionEn: kaiserGaSoldPolicyDescriptionEn,
            descriptionEs: kaiserGaSoldPolicyDescriptionEs,
            description: kaiserGaSoldPolicyDescriptionEn,
            accept: null,
            expires: false,
            required: true,
            state: 'Georgia',
            name: 'Kaiser_GA_Sold_Policy_Ack',
            type: 'kaiserGaSoldPolicyAcknowledgement',
            carrierBased: false,
            defaultExpirationDate: null
          }
        ]
      : [];

  const suncareRequiredActions =
    isSuncareContext(carrierData.name) && user
      ? buildSuncareRequiredActions(getSuncarePhase(user))
      : [];

  const sortedCompleteRequiredActions = [
    ...suncareRequiredActions,
    ...kaiserGaSoldPolicyAcknowledgementStep,
    ...sortedBase
  ];

  // get the required files based on the sorted required actions
  const filteredActions = getFilteredActions(
    sortedCompleteRequiredActions,
    attachments,
    carrierData.name
  );

  return {
    filteredActions,
    sortedCompleteRequiredActions,
    isContractingBlackout,
    contractingBlackoutStates,
    isFullContractingBlackout,
    isTransferBlackout,
    transferBlackoutStates,
    isFullTransferBlackout
  };
}
